import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NavigationEnd, Router } from '@angular/router';
import { filter, finalize } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { ContactUs } from '../../services/contact-us/contact-us';
import { ContactUsService } from '../../services/contact-us/contact-us.service';
import { ErrorComponent, InformationComponent } from '../../util/helpers';
import { TrackerService } from '../../util/tracker.service';
import { environment } from 'src/environments/environment';
import { SolutionPageConfig } from './solution-page.model';

const SCROLL_HEADER_OFFSET = 80;

@Component({
  selector: 'app-solution-page',
  templateUrl: './solution-page.component.html',
  styleUrls: ['./solution-page.component.scss'],
})
export class SolutionPageComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() config!: SolutionPageConfig;

  contactItem: ContactUs = new ContactUs();
  assistantHref = '';
  isLoading = false;
  contactSuccess = false;
  openFaqIndex: number | null = 0;

  private routerEventsSub: Subscription | null = null;
  private fragmentScrollTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private snackBar: MatSnackBar,
    private contactUsService: ContactUsService,
    private router: Router,
    private trackerService: TrackerService
  ) {}

  ngOnInit(): void {
    this.trackerService.trackPageViews();
    this.assistantHref = environment.assistantApp;

    this.contactItem.subject = '';
    this.contactItem.content = '';
    this.contactItem.email = '';
    this.contactItem.telephone = '';

    this.routerEventsSub = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => {
        const fragment = this.getUrlFragment();
        if (fragment) {
          this.scheduleScrollToFragment(fragment);
        }
      });

    const initialFragment = this.getUrlFragment();
    if (initialFragment) {
      this.scheduleScrollToFragment(initialFragment);
    }
  }

  ngAfterViewInit(): void {
    const fragment = this.getUrlFragment();
    if (fragment) {
      this.scheduleScrollToFragment(fragment);
    }
  }

  ngOnDestroy(): void {
    this.routerEventsSub?.unsubscribe();
    if (this.fragmentScrollTimer) {
      clearTimeout(this.fragmentScrollTimer);
    }
  }

  tKey(suffix: string): string {
    return `${this.config.i18nPrefix}.${suffix}`;
  }

  heroSubtitleKey(): string {
    const key = this.config.heroSubtitleKey || 'TEXT';
    return this.tKey(key);
  }

  shortTitleKey(index: number): string {
    return this.tKey(`FEATURE_${index}_SHORT_TITLE`);
  }

  shortTextKey(index: number): string {
    return this.tKey(`FEATURE_${index}_SHORT_TEXT`);
  }

  questionKey(index: number): string {
    return this.tKey(`QUESTION_${index}`);
  }

  questionAnswerKey(index: number): string {
    return this.tKey(`QUESTION_${index}_ANSWER`);
  }

  scrollToFaq(): void {
    this.scrollToFragment('faq', 0);
  }

  toggleFaq(index: number): void {
    this.openFaqIndex = this.openFaqIndex === index ? null : index;
  }

  send(): void {
    if (!this.contactItem.subject || !this.contactItem.email || !this.contactItem.content) {
      this.snackBar.openFromComponent(ErrorComponent, {
        data: { message: 'Vă rugăm să completați toate câmpurile.' },
        duration: 2000,
      });
      return;
    }

    this.isLoading = true;
    this.contactSuccess = false;
    this.contactItem.date = new Date();

    this.contactUsService
      .addItem(this.contactItem)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe(
        () => {
          this.contactItem.subject = '';
          this.contactItem.email = '';
          this.contactItem.content = '';
          this.contactSuccess = true;
          this.snackBar.openFromComponent(InformationComponent, {
            data: { message: 'Mesajul a fost trimis cu succes!' },
            duration: 2000,
          });
        },
        () => {
          this.snackBar.openFromComponent(ErrorComponent, {
            data: { message: 'A apărut o eroare. Vă rugăm să încercați din nou.' },
            duration: 2000,
          });
        }
      );
  }

  private getUrlFragment(): string | null {
    return this.router.parseUrl(this.router.url).fragment;
  }

  private scheduleScrollToFragment(fragment: string): void {
    if (this.fragmentScrollTimer) {
      clearTimeout(this.fragmentScrollTimer);
    }
    this.fragmentScrollTimer = setTimeout(() => this.scrollToFragment(fragment, 0), 50);
  }

  private scrollToFragment(fragment: string, attempt: number): void {
    const target = document.getElementById(fragment);
    if (target) {
      const top =
        target.getBoundingClientRect().top + window.pageYOffset - SCROLL_HEADER_OFFSET;
      window.scrollTo({
        top: Math.max(0, top),
        behavior: attempt === 0 ? 'smooth' : 'auto',
      });
      return;
    }

    if (attempt < 20) {
      setTimeout(() => this.scrollToFragment(fragment, attempt + 1), 100);
    }
  }
}
